import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../store/useStore';
import { supabase } from '../lib/supabase';
import { Loader2, FileText, Package, PackagePlus, Plus, Trash2, X, Building, Eye, ArrowUp, ArrowDown, UserCheck, PencilLine, ChevronDown, ChevronUp } from 'lucide-react';
import type { Database } from '../types/database.types';
import { CustomerSelectionModal } from '../components/CustomerSelectionModal';
import ProductSelectionModal from '../components/ProductSelectionModal';
import { ProductFormModal } from '../components/ProductFormModal';
import { PriceListPrintView } from './PriceListPrintView';
import CreateInvoiceFromOfferModal from '../components/CreateInvoiceFromOfferModal';
import { InvoiceDetailPage } from './InvoiceDetailPage';
import { FloatingWindow } from '../components/ui/floating-window';
import {
  priceListLineTotalAmount,
  PRICE_LIST_CONSUMPTION_LABEL,
  isConsumptionCategoryItem,
} from '../lib/priceListItemPricing';
import ManualLineModal, { type ManualLineValues } from '../components/ManualLineModal';
import { isCustomLine, normalizePriceListItems } from '../lib/priceListCustomLines';

type PriceList = Database['public']['Tables']['price_lists']['Row'];
type PriceListItem = Database['public']['Tables']['price_list_items']['Row'];
type Customer = Database['public']['Tables']['customers']['Row'];

interface Product {
  id: string;
  code: string;
  name: string;
  category?: string;
  unit: string;
  base_price: number;
  photo_url?: string | null;
  cartone?: string | null;
  pallet?: string | null;
  scadenza?: string | null;
}

interface PriceListWithItems extends PriceList {
  customer?: Customer;
  price_list_items: (PriceListItem & {
    products: Product;
  })[];
}

type PriceListItemWithProduct = PriceListWithItems['price_list_items'][number];

interface PriceListDetailPageProps {
  isOpen: boolean;
  onClose: () => void;
  priceListId?: string;
  onSaveSuccess: (newPriceListId?: string) => void;
}

const priceListSchema = z.object({
  name: z.string().min(1, 'Nome offerta richiesto'),
  description: z.string().optional(),
  customer_id: z.string().optional(),
  valid_from: z.string().optional(),
  valid_until: z.string().optional(),
  currency: z.string().default('EUR'),
  is_default: z.boolean().default(false),
  print_conditions: z.boolean().default(true),
  print_discount: z.boolean().default(true),
  print_attachments: z.boolean().default(true),
  no_vat_forfettario: z.boolean().default(true),
  no_vat_clause_text: z.string().optional(),
  payment_conditions: z.string().optional(),
  shipping_conditions: z.string().optional(),
  delivery_conditions: z.string().optional(),
  brand_conditions: z.string().optional(),
  notes: z.string().optional(),
});

type PriceListFormData = z.infer<typeof priceListSchema>;

export function PriceListDetailPage({
  isOpen,
  onClose,
  priceListId,
  onSaveSuccess,
}: PriceListDetailPageProps) {
  const { addNotification } = useNotifications();
  const { user } = useAuth();

  const productsSectionRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPriceList, setCurrentPriceList] = useState<PriceListWithItems | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [showNewProductModal, setShowNewProductModal] = useState(false);
  const [productCategories, setProductCategories] = useState<string[]>([]);
  const [showManualLineModal, setShowManualLineModal] = useState(false);
  const [editingManualItem, setEditingManualItem] = useState<PriceListItemWithProduct | null>(null);
  const [isSavingManualLine, setIsSavingManualLine] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [sortField, setSortField] = useState<'code' | 'name'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [printByCategory, setPrintByCategory] = useState(false);
  // Su telefono i 5 campi dell'intestazione riempiono lo schermo e spingono la
  // lista prodotti fuori dalla vista: si aprono a richiesta. Da `sm` in su
  // restano sempre visibili come prima.
  const [showOfferInfo, setShowOfferInfo] = useState(false);
  const [showConditions, setShowConditions] = useState(false);
  const [canoneMonths, setCanoneMonths] = useState<number>(6);
  const [pendingMOQValues, setPendingMOQValues] = useState<Record<string, string>>({});
  const [pendingPriceValues, setPendingPriceValues] = useState<Record<string, string>>({});
  const [pendingDiscountValues, setPendingDiscountValues] = useState<Record<string, string>>({});
  const [commerciali, setCommerciali] = useState<{ id: string; full_name: string | null; email: string }[]>([]);
  const [selectedCommercialeId, setSelectedCommercialeId] = useState<string>('');
  const [offerStatus, setOfferStatus] = useState<'draft' | 'sent' | 'accepted' | 'partially_invoiced' | 'invoiced' | 'expired'>('draft');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [reviewInvoiceId, setReviewInvoiceId] = useState<string | null>(null);
  const conditionsSaveTimerRef = useRef<number | null>(null);

  const scheduleSaveConditions = (immediate = false, targetPriceListId?: string) => {
    const priceListId = targetPriceListId || currentPriceList?.id;
    if (!priceListId) return;
    if (conditionsSaveTimerRef.current) {
      window.clearTimeout(conditionsSaveTimerRef.current);
      conditionsSaveTimerRef.current = null;
    }
    const run = async () => {
      try {
        const values = getValues();

        // Helper per conversione data in ISO
        const convertDateToISO = (dateStr: string | undefined | null): string | null => {
          if (!dateStr) return null;
          if (dateStr.includes('-')) return dateStr;
          if (dateStr.includes('/')) {
            const [day, month, year] = dateStr.split('/');
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
          return dateStr;
        };

        const { error } = await (supabase.from('price_lists') as any)
          .update({
            payment_conditions: values.payment_conditions || null,
            // shipping_conditions: values.shipping_conditions || null,
            delivery_conditions: values.delivery_conditions || null,
            // brand_conditions: values.brand_conditions || null,
            notes: values.notes || null,
            valid_from: convertDateToISO(values.valid_from),
            valid_until: convertDateToISO(values.valid_until),
            print_conditions: values.print_conditions ?? true,
            print_discount: values.print_discount ?? true,
            print_attachments: values.print_attachments ?? true,
            no_vat_forfettario: values.no_vat_forfettario ?? false,
            no_vat_clause_text: values.no_vat_clause_text?.trim() || null,
          })
          .eq('id', priceListId);
        if (error) {
          // Fallback: se la colonna non esiste ancora, riprova senza print_conditions
          if (
            (error as any)?.code === 'PGRST204' ||
            String((error as any)?.message || '').includes("Could not find the 'print_conditions' column")
          ) {
            const { error: retryError } = await (supabase.from('price_lists') as any)
              .update({
                payment_conditions: values.payment_conditions || null,
                // shipping_conditions: values.shipping_conditions || null,
                delivery_conditions: values.delivery_conditions || null,
                // brand_conditions: values.brand_conditions || null,

                notes: values.notes || null,
                valid_from: convertDateToISO(values.valid_from),
                valid_until: convertDateToISO(values.valid_until),
              })
              .eq('id', priceListId);
            if (retryError) throw retryError;
          } else {
            throw error;
          }
        }
      } catch (e) {
        console.error('Errore salvataggio condizioni:', e);
      }
    };
    if (immediate) {
      void run();
    } else {
      conditionsSaveTimerRef.current = window.setTimeout(run, 600);
    }
  };

  const applyCustomerPaymentTerms = (
    customer: Customer | null | undefined,
    persistImmediately = true,
    targetPriceListId?: string
  ) => {
    if (!customer) return;
    const paymentTerms = customer.payment_terms || '';
    setValue('payment_conditions', paymentTerms);
    if (persistImmediately) {
      scheduleSaveConditions(true, targetPriceListId);
    }
  };

  // Main form
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
  } = useForm<PriceListFormData>({
    resolver: zodResolver(priceListSchema),
    defaultValues: {
      currency: 'EUR',
      is_default: false,
      print_conditions: true,
      print_discount: true,
      print_attachments: true,
      no_vat_forfettario: true,
      no_vat_clause_text: '',
      delivery_conditions: '1 anno',
    },
  });

  const noVatForfettarioWatched = watch('no_vat_forfettario');
  const paymentConditionsWatched = watch('payment_conditions');
  const deliveryConditionsWatched = watch('delivery_conditions');
  const validFromWatched = watch('valid_from');
  const validUntilWatched = watch('valid_until');
  const conditionsSummary = [
    paymentConditionsWatched,
    deliveryConditionsWatched,
    [validFromWatched, validUntilWatched].filter(Boolean).join(' → ')
  ]
    .map((value) => (value ? String(value).trim() : ''))
    .filter(Boolean)
    .join(' · ') || 'Nessuna condizione impostata';

  // All'apertura mostro i dati dell'offerta solo se è nuova: su un'offerta esistente
  // lo spazio serve alle righe prodotto.
  useEffect(() => {
    if (!isOpen) return;
    setShowOfferInfo(!priceListId);
    setShowConditions(false);
  }, [isOpen, priceListId]);


  const loadData = async () => {
    try {
      setIsLoading(true);

      // Load customers
      const { data: customersData, error: customersError } = await (supabase.from('customers') as any)
        .select('id, company_name, contact_person, payment_terms, email')
        .eq('is_active', true)
        .order('company_name');

      if (customersError) throw customersError;


      setCustomers(customersData || []);

      // Load ALL profiles for assignment (admin can assign to anyone)
      const { data: commercialiData } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .order('full_name');
      setCommerciali(commercialiData || []);

      // Load price list if editing
      if (priceListId) {
        await fetchPriceListDetails(priceListId);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Impossibile caricare i dati',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPriceListDetails = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('price_lists')
        .select(`
          *,
          price_list_items (
            *,
            products (id, code, name, category, unit, base_price, cartone, pallet, scadenza)
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      const rawPriceList = data as PriceListWithItems;
      // Le righe manuali (product_id null) ricevono un prodotto sintetico dai campi custom_*
      const priceList: PriceListWithItems = {
        ...rawPriceList,
        price_list_items: normalizePriceListItems(rawPriceList.price_list_items),
      };

      // Find customer associated with this price list via price_lists.customer_id
      let customerData: Customer | null = null;
      if ((priceList as any).customer_id) {
        const { data: c } = await (supabase.from('customers') as any)
          .select('*')
          .eq('id', (priceList as any).customer_id)
          .maybeSingle();
        customerData = c || null;
      }

      setCurrentPriceList({
        ...priceList,
        customer: customerData || undefined,
      } as PriceListWithItems);

      // Converti date dal formato ISO (yyyy-mm-dd) al formato italiano (dd/mm/yyyy)
      const convertDateToItalian = (dateStr: string | null): string => {
        if (!dateStr) return '';
        // Se è in formato ISO, convertilo
        if (dateStr.includes('-')) {
          const [year, month, day] = dateStr.split('-');
          return `${day}/${month}/${year}`;
        }
        return dateStr;
      };

      // Pre-fill form
      reset({
        name: priceList.name,
        description: priceList.description || '',
        valid_from: convertDateToItalian(priceList.valid_from),
        valid_until: convertDateToItalian(priceList.valid_until),
        currency: priceList.currency,
        is_default: priceList.is_default,
        print_conditions: (priceList as any).print_conditions ?? true,
        print_discount: (priceList as any).print_discount ?? true,
        print_attachments: (priceList as any).print_attachments ?? true,
        no_vat_forfettario: (priceList as any).no_vat_forfettario ?? false,
        no_vat_clause_text: (priceList as any).no_vat_clause_text || '',
        customer_id: customerData?.id || '',
        payment_conditions: priceList.payment_conditions || '',
        shipping_conditions: priceList.shipping_conditions || '',
        delivery_conditions: priceList.delivery_conditions || '1 anno',
        brand_conditions: priceList.brand_conditions || '',
        notes: priceList.notes || '',
      });

      const resolvedCustomerId = customerData?.id || '';
      setSelectedCustomerId(resolvedCustomerId);
      setSelectedCustomer(customerData || null);
      setSelectedCommercialeId((priceList as any).created_by || '');
      setCanoneMonths((priceList as any).canone_months ?? 6);
      setOfferStatus(((priceList as any).status ?? 'draft') as any);

      if ((!priceList.payment_conditions || priceList.payment_conditions.trim() === '') && customerData?.payment_terms) {
        setValue('payment_conditions', customerData.payment_terms);
        scheduleSaveConditions(true, priceList.id);
      }
    } catch (error) {
      console.error('Error fetching price list details:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Impossibile caricare i dettagli dell\'offerta',
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
      // Offerta nuova: l'intestazione è la prima cosa da compilare, quindi aperta.
      // Offerta esistente: si entra per lavorare sui prodotti, quindi chiusa.
      setShowOfferInfo(!priceListId);
      // Imposta il commerciale di default all'utente corrente per nuove offerte
      if (!priceListId && user?.id) {
        setSelectedCommercialeId(user.id);
      }
      // Prevenire lo scroll del body quando la modale è aperta
      document.body.style.overflow = 'hidden';
    } else {
      // Ripristinare lo scroll del body quando la modale è chiusa
      document.body.style.overflow = 'unset';
    }

    // Cleanup quando il componente viene smontato
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, priceListId]);

  const handleMainFormSubmit = async (data: PriceListFormData) => {
    if (!user?.id) {
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Devi essere autenticato per creare o modificare offerte'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Extract customer_id from form data - it doesn't belong in price_lists table
      const { customer_id, ...priceListData } = data;
      // ID cliente che vogliamo associare dopo il salvataggio
      const effectiveCustomerId = customer_id || selectedCustomerId || '';

      // Converti date dal formato italiano (dd/mm/yyyy) al formato ISO (yyyy-mm-dd)
      const convertDateToISO = (dateStr: string | undefined): string | null => {
        if (!dateStr) return null;
        // Se è già in formato ISO, restituiscilo
        if (dateStr.includes('-')) return dateStr;
        // Se è in formato italiano (dd/mm/yyyy), convertilo
        if (dateStr.includes('/')) {
          const [day, month, year] = dateStr.split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return dateStr;
      };

      const sanitizedPriceListData = {
        ...priceListData,
        description: data.description || null,
        valid_from: convertDateToISO(data.valid_from),
        valid_until: convertDateToISO(data.valid_until),
        print_conditions: data.print_conditions ?? true,
        print_discount: data.print_discount ?? true,
        print_attachments: data.print_attachments ?? true,
        payment_conditions: data.payment_conditions || null,
        shipping_conditions: data.shipping_conditions || null,
        delivery_conditions: data.delivery_conditions || null,
        brand_conditions: data.brand_conditions || null,
        notes: data.notes || null,
        no_vat_forfettario: data.no_vat_forfettario ?? false,
        no_vat_clause_text: data.no_vat_clause_text?.trim() || null,
        customer_id: effectiveCustomerId || null,
        canone_months: canoneMonths,
      };

      if (currentPriceList) {
        // Update existing price list (include created_by for reassignment)
        const updatePayload = {
          ...sanitizedPriceListData,
          ...(selectedCommercialeId ? { created_by: selectedCommercialeId } : {}),
        };
        const { error } = await (supabase.from('price_lists') as any)
          .update(updatePayload)
          .eq('id', currentPriceList.id);

        if (error) {
          // Fallback se la colonna non esiste ancora: riprova senza print_conditions
          if (
            (error as any)?.code === 'PGRST204' ||
            String((error as any)?.message || '').includes("Could not find the 'print_conditions' column")
          ) {
            const { print_conditions, print_discount, print_attachments, ...fallbackData } = sanitizedPriceListData as any;
            const { error: retryError } = await (supabase.from('price_lists') as any)
              .update(fallbackData)
              .eq('id', currentPriceList.id);
            if (retryError) throw retryError;
          } else {
            throw error;
          }
        }

        addNotification({
          type: 'success',
          title: 'Offerta aggiornata',
          message: `${data.name} è stata aggiornata con successo`,
        });

        // Gestione associazione cliente <-> listino
        // Ora è tutta gestita tramite la colonna price_lists.customer_id,
        // quindi qui ci limitiamo ad aggiornare lo stato locale in base a effectiveCustomerId
        if (effectiveCustomerId) {
          setSelectedCustomerId(effectiveCustomerId);
          setSelectedCustomer(
            customers.find(c => c.id === effectiveCustomerId) || null
          );
        } else {
          setSelectedCustomerId('');
          setSelectedCustomer(null);
        }

        // Refresh the price list data
        await fetchPriceListDetails(currentPriceList.id);

        // Call success callback when updating existing price list
        onSaveSuccess();
      } else {
        // Create new price list
        const newPriceListPayload = {
          ...sanitizedPriceListData,
          created_by: selectedCommercialeId || user.id
        };

        let insertResult = await (supabase.from('price_lists') as any)
          .insert([newPriceListPayload])
          .select()
          .single();

        if (insertResult.error) {
          // Fallback se la colonna non esiste: riprova senza print_conditions
          const err = insertResult.error as any;
          if (
            err?.code === 'PGRST204' ||
            String(err?.message || '').includes("Could not find the 'print_conditions' column")
          ) {
            const { print_conditions, print_discount, print_attachments, ...fallbackPayload } = newPriceListPayload as any;
            insertResult = await (supabase.from('price_lists') as any)
              .insert([fallbackPayload])
              .select()
              .single();
            if (insertResult.error) throw insertResult.error;
          } else {
            throw insertResult.error;
          }
        }
        const newPriceList = insertResult.data!;

        // Handle customer assignment for new price list:
        // impostiamo direttamente customer_id sul nuovo listino
        if (effectiveCustomerId) {
          const { error: customerError } = await (supabase.from('price_lists') as any)
            .update({ customer_id: effectiveCustomerId })
            .eq('id', newPriceList.id);

          if (customerError) {
            console.error('Error assigning customer to new price list:', customerError);
            addNotification({
              type: 'warning',
              title: 'Attenzione',
              message: 'Offerta creata ma errore nell\'assegnazione del cliente',
            });
          } else {
            setSelectedCustomerId(effectiveCustomerId);
            setSelectedCustomer(
              customers.find(c => c.id === effectiveCustomerId) || null
            );
          }
        }

        addNotification({
          type: 'success',
          title: 'Offerta creata',
          message: `${data.name} è stata creata con successo`,
        });

        // Set the current price list to enable product management
        setCurrentPriceList({
          ...newPriceList,
          price_list_items: [],
        } as PriceListWithItems);

        // Auto-scroll to products section
        setTimeout(() => {
          productsSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 500);

        // Call success callback to refresh the list when creating a new price list
        onSaveSuccess(newPriceList.id);
      }
    } catch (error: any) {
      console.error('Error saving price list:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: error.message || 'Errore durante il salvataggio dell\'offerta',
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleDeleteItem = async (itemId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo prodotto dall\'offerta?')) return;

    try {
      const { error } = await supabase
        .from('price_list_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      addNotification({
        type: 'success',
        title: 'Prodotto rimosso',
        message: 'Il prodotto è stato rimosso dall\'offerta',
      });

      // Refresh the price list data
      if (currentPriceList) {
        await fetchPriceListDetails(currentPriceList.id);
      }
    } catch (error: any) {
      console.error('Error deleting price list item:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore durante la rimozione del prodotto',
      });
    }
  };


  /** Apre la maschera del catalogo; le categorie arrivano mentre è già aperta. */
  const handleOpenNewProductModal = async () => {
    setShowNewProductModal(true);
    const { data, error } = await supabase
      .from('categories')
      .select('name')
      .eq('is_active', true)
      .order('name');
    if (error) {
      console.error('Errore nel caricamento categorie:', error);
      return;
    }
    setProductCategories((data || []).map(cat => cat.name).filter((name): name is string => Boolean(name)));
  };

  /** Il prodotto appena creato nel catalogo entra subito nell'offerta aperta. */
  const handleNewProductCreated = async (product?: { id: string; name: string; base_price: number | null }) => {
    if (!product || !currentPriceList) return;

    const { error } = await supabase.from('price_list_items').insert({
      price_list_id: currentPriceList.id,
      product_id: product.id,
      price: product.base_price ?? 0,
      min_quantity: 1,
      discount_percentage: 0,
    });

    if (error) {
      console.error('Errore aggiunta nuovo prodotto all\'offerta:', error);
      addNotification({
        type: 'error',
        title: 'Prodotto creato, ma non aggiunto',
        message: 'Il prodotto è nel catalogo: aggiungilo con "Aggiungi Prodotti".',
      });
      return;
    }

    await fetchPriceListDetails(currentPriceList.id);
    addNotification({
      type: 'success',
      title: 'Aggiunto all\'offerta',
      message: `"${product.name}" è stato aggiunto all'offerta`,
    });
  };

  const handleOpenManualLineModal = (item?: PriceListItemWithProduct) => {
    setEditingManualItem(item ?? null);
    setShowManualLineModal(true);
  };

  const handleCloseManualLineModal = () => {
    setShowManualLineModal(false);
    setEditingManualItem(null);
  };

  /** Inserisce o aggiorna una riga libera (product_id null + campi custom_*). */
  const handleSaveManualLine = async (values: ManualLineValues) => {
    if (!currentPriceList) return;

    setIsSavingManualLine(true);
    try {
      const payload = {
        product_id: null,
        custom_code: values.code || null,
        custom_name: values.name,
        custom_description: values.description || null,
        custom_unit: values.unit || null,
        custom_category: values.category || null,
        price: values.price,
        min_quantity: values.quantity,
        discount_percentage: values.discountPercentage,
      };

      if (editingManualItem) {
        const { error } = await (supabase.from('price_list_items') as any)
          .update(payload)
          .eq('id', editingManualItem.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase.from('price_list_items') as any).insert({
          ...payload,
          price_list_id: currentPriceList.id,
        });
        if (error) throw error;
      }

      await fetchPriceListDetails(currentPriceList.id);

      addNotification({
        type: 'success',
        title: editingManualItem ? 'Riga aggiornata' : 'Riga aggiunta',
        message: `"${values.name}" ${editingManualItem ? 'aggiornata' : 'aggiunta'} all'offerta`,
      });

      handleCloseManualLineModal();
    } catch (error: any) {
      console.error('Errore salvataggio riga manuale:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: error?.message || 'Errore durante il salvataggio della riga manuale',
      });
    } finally {
      setIsSavingManualLine(false);
    }
  };

  const handleClose = () => {
    reset();
    setCurrentPriceList(null);
    setSelectedCustomerId('');
    onClose();
  };

  const handleSaveAndClose = async () => {
    try {
      // Usa handleSubmit per validare e salvare i dati del form
      await handleSubmit(handleMainFormSubmit)();

      // Dopo aver salvato il listino, salva anche i campi prodotto (cartone, pallet, scadenza)
      if (currentPriceList?.price_list_items?.length) {
        // Raggruppa per prodotto per evitare update duplicati
        const productUpdatesMap = new Map<
          string,
          { cartone: string | null; pallet: string | null; scadenza: string | null }
        >();

        currentPriceList.price_list_items.forEach((item) => {
          const product = item.products;
          if (!product?.id) return;

          productUpdatesMap.set(product.id, {
            cartone: (product as any).cartone ?? null,
            pallet: (product as any).pallet ?? null,
            scadenza: (product as any).scadenza ?? null,
          });
        });

        for (const [productId, fields] of productUpdatesMap.entries()) {
          const { error } = await (supabase.from('products') as any)
            .update(fields)
            .eq('id', productId);

          if (error) {
            console.error('Errore nel salvataggio dei campi prodotto:', error);
            addNotification({
              type: 'error',
              title: 'Errore',
              message: 'Errore nel salvataggio dei dati prodotto (cartone/pedana/scadenza)',
            });
            // Non rilanciamo l'errore: continuiamo a chiudere la modale per non bloccare l’utente
          }
        }
      }

      // Se arriviamo qui, il salvataggio è andato a buon fine
      addNotification({
        type: 'success',
        title: 'Offerta salvata',
        message: 'Le modifiche sono state salvate con successo'
      });

      // Chiudi la modale
      handleClose();
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nel salvataggio delle modifiche'
      });
    }
  };


  const handleCustomerChange = (customerId: string, customerOverride?: Customer | null) => {
    setValue('customer_id', customerId);
    setSelectedCustomerId(customerId);

    // Find and set the selected customer object
    const customer = customerOverride || customers.find(c => c.id === customerId) || null;
    setSelectedCustomer(customer);

    if (customer) {
      applyCustomerPaymentTerms(customer, true);
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    handleCustomerChange(customer.id, customer);
    setShowCustomerModal(false);
  };


  const handleOpenCustomerModal = () => {
    setShowCustomerModal(true);
  };




  const handlePriceChange = async (itemId: string, newPrice: number) => {
    try {
      // Normalizza sempre a 2 decimali
      const normalizedPrice = Number.isFinite(newPrice)
        ? Number(newPrice.toFixed(2))
        : 0;

      const { error } = await (supabase.from('price_list_items') as any)
        .update({ price: normalizedPrice })
        .eq('id', itemId);

      if (error) throw error;

      // Aggiorna lo stato locale
      if (currentPriceList) {
        const updatedItems = currentPriceList.price_list_items.map(item =>
          item.id === itemId ? { ...item, price: normalizedPrice } : item
        );
        setCurrentPriceList({ ...currentPriceList, price_list_items: updatedItems });
      }

      // Pulisci il valore temporaneo per questo item
      setPendingPriceValues(prev => {
        if (!(itemId in prev)) return prev;
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      console.error('Errore nell\'aggiornamento del prezzo:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nell\'aggiornamento del prezzo'
      });
    }
  };

  const handleMOQChange = async (itemId: string, newMOQ: number) => {
    try {
      const { error } = await (supabase.from('price_list_items') as any)
        .update({ min_quantity: newMOQ })
        .eq('id', itemId);

      if (error) throw error;

      // Aggiorna lo stato locale
      if (currentPriceList) {
        const updatedItems = currentPriceList.price_list_items.map(item =>
          item.id === itemId ? { ...item, min_quantity: newMOQ } : item
        );
        setCurrentPriceList({ ...currentPriceList, price_list_items: updatedItems });
      }

      setPendingMOQValues(prev => {
        if (!(itemId in prev)) return prev;
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      console.error('Errore nell\'aggiornamento del MOQ:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nell\'aggiornamento del MOQ'
      });
    }
  };

  const handleDiscountChange = async (itemId: string, newDiscount: number) => {
    try {
      const { error } = await (supabase.from('price_list_items') as any)
        .update({ discount_percentage: newDiscount })
        .eq('id', itemId);

      if (error) throw error;

      // Aggiorna lo stato locale
      if (currentPriceList) {
        const updatedItems = currentPriceList.price_list_items.map(item =>
          item.id === itemId ? { ...item, discount_percentage: newDiscount } : item
        );
        setCurrentPriceList({ ...currentPriceList, price_list_items: updatedItems });
      }

      setPendingDiscountValues(prev => {
        if (!(itemId in prev)) return prev;
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      console.error('Errore nell\'aggiornamento dello sconto:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nell\'aggiornamento dello sconto'
      });
    }
  };

  const renderProductCard = (item: PriceListItemWithProduct) => {
    const isConsumptionLine = isConsumptionCategoryItem(item);
    const isManualLine = isCustomLine(item);

    return (
    <div
      key={item.id}
      className={`bg-white border rounded p-2 ${isManualLine ? 'border-blue-300' : 'border-green-200'}`}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 sm:flex-wrap xl:flex-nowrap">
        <div className="flex items-center gap-2 w-full min-w-0 sm:min-w-[220px] sm:flex-1">
          {item.products.photo_url && (
            <img
              src={item.products.photo_url}
              alt={item.products.name}
              className="w-8 h-8 object-cover rounded border"
            />
          )}
          <div className="min-w-0">
            <p className="font-medium text-gray-900 text-xs flex flex-wrap items-center gap-1 break-words">
              {item.products.name}
              {isManualLine && (
                <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1">
                  Manuale
                </span>
              )}
            </p>
            <p className="text-xs text-gray-500">{item.products.code}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:contents">
        <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[100px]">
          <span className="text-xs text-gray-600 w-full sm:w-auto">Sconto:</span>
          <div className="relative flex-1 min-w-0 sm:flex-none">
            <Input
              type="text"
              inputMode="decimal"
              disabled={isConsumptionLine}
              value={pendingDiscountValues[item.id] ?? (item.discount_percentage?.toString() ?? '0')}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                  setPendingDiscountValues(prev => ({
                    ...prev,
                    [item.id]: value,
                  }));
                }
              }}
              onBlur={async () => {
                const raw = pendingDiscountValues[item.id] ?? (item.discount_percentage?.toString() ?? '0');
                if (raw === '') {
                  setPendingDiscountValues(prev => {
                    if (!(item.id in prev)) return prev;
                    const { [item.id]: _removed, ...rest } = prev;
                    return rest;
                  });
                  return;
                }
                const parsed = parseFloat(raw.replace(',', '.'));
                if (isNaN(parsed)) return;
                const normalized = Number(parsed.toFixed(2));
                if (normalized !== Number(item.discount_percentage ?? 0)) {
                  await handleDiscountChange(item.id, normalized);
                } else {
                  setPendingDiscountValues(prev => {
                    if (!(item.id in prev)) return prev;
                    const { [item.id]: _removed, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              className="h-5 text-xs w-full sm:w-14 pr-4 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-50"
            />
            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-gray-500">%</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[110px]">
          <span className="text-xs text-gray-600 w-full sm:w-auto">Prezzo:</span>
          {isConsumptionLine ? (
            <span className="text-xs font-semibold text-teal-800 px-1.5 py-0.5 bg-teal-50 rounded border border-teal-200">
              {PRICE_LIST_CONSUMPTION_LABEL}
            </span>
          ) : (
            <div className="relative flex-1 min-w-0 sm:flex-none">
              <Input
                type="text"
                inputMode="decimal"
                value={
                  pendingPriceValues[item.id] ??
                  (item.price !== null && item.price !== undefined
                    ? String(item.price)
                    : '')
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                    setPendingPriceValues(prev => ({
                      ...prev,
                      [item.id]: value,
                    }));
                  }
                }}
                onBlur={async () => {
                  const raw =
                    pendingPriceValues[item.id] ??
                    (item.price !== null && item.price !== undefined
                      ? String(item.price)
                      : '');

                  if (raw === '') {
                    setPendingPriceValues(prev => {
                      if (!(item.id in prev)) return prev;
                      const { [item.id]: _removed, ...rest } = prev;
                      return rest;
                    });
                    return;
                  }

                  const parsed = parseFloat(raw.replace(',', '.'));
                  if (isNaN(parsed)) {
                    setPendingPriceValues(prev => {
                      if (!(item.id in prev)) return prev;
                      const { [item.id]: _removed, ...rest } = prev;
                      return rest;
                    });
                    return;
                  }

                  const normalized = Number(parsed.toFixed(2));
                  if (normalized !== Number(item.price ?? 0)) {
                    await handlePriceChange(item.id, normalized);
                  } else {
                    setPendingPriceValues(prev => {
                      if (!(item.id in prev)) return prev;
                      const { [item.id]: _removed, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                className="h-5 text-xs w-full sm:w-16 pr-4 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-gray-500">€</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[120px]">
          <span className="text-xs text-gray-600 w-full sm:w-auto">Qta:</span>
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pendingMOQValues[item.id] ?? (item.min_quantity?.toString() ?? '')}
            onChange={(e) => {
              const { value } = e.target;
              if (/^\d*$/.test(value)) {
                setPendingMOQValues(prev => ({
                  ...prev,
                  [item.id]: value
                }));
              }
            }}
            onBlur={() => {
              const rawValue = pendingMOQValues[item.id] ?? (item.min_quantity?.toString() ?? '');
              const parsedValue = parseInt(rawValue, 10);
              const finalValue = !isNaN(parsedValue) && parsedValue > 0 ? parsedValue : item.min_quantity || 1;
              if (finalValue !== item.min_quantity) {
                handleMOQChange(item.id, finalValue);
              } else if (pendingMOQValues[item.id] !== undefined) {
                setPendingMOQValues(prev => {
                  const { [item.id]: _removed, ...rest } = prev;
                  return rest;
                });
              }
            }}
            className="h-5 text-xs flex-1 min-w-0 sm:flex-none sm:w-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-xs text-gray-500">{item.products.unit}</span>
        </div>



        </div>

        <div className="flex gap-2 sm:contents">
        {isManualLine && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleOpenManualLineModal(item)}
            title="Modifica riga manuale"
            className="h-8 flex-1 sm:h-5 sm:flex-none text-xs px-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
          >
            <PencilLine className="w-3 h-3" />
            <span className="ml-1 sm:hidden">Modifica</span>
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDeleteItem(item.id)}
          className="h-8 flex-1 sm:h-5 sm:flex-none text-xs px-1 text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-3 h-3" />
          <span className="ml-1 sm:hidden">Rimuovi</span>
        </Button>
        </div>
      </div>
    </div>
    );
  };

  const windowTitle = priceListId ? 'Modifica Offerta' : 'Nuova Offerta';

  return (
    <>
      <FloatingWindow
        isOpen={isOpen}
        onClose={handleClose}
        title={
          <span className="flex items-baseline gap-2 min-w-0">
            <span className="truncate">{windowTitle}</span>
            {selectedCustomer && (
              <span className="hidden sm:inline text-xs font-normal text-gray-500 truncate">
                {selectedCustomer.company_name}
              </span>
            )}
          </span>
        }
        minimizedLabel={currentPriceList?.name || windowTitle}
        storageKey="offer-editor"
      >
        {/* Content */}
        <div className="flex-1 flex flex-col px-2 py-1 overflow-hidden min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="flex-1 flex flex-col min-h-0">
              {/* Informazioni Listino Section - Immediatamente sotto i pulsanti */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-1 mt-0 flex-shrink-0">
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setShowOfferInfo((open) => !open)}
                    aria-expanded={showOfferInfo}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left py-0.5"
                  >
                    <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <h3 className="text-sm font-semibold text-red-800 flex-shrink-0">Informazioni Offerta</h3>
                    {!showOfferInfo && (
                      <span className="text-xs font-normal text-red-700/80 truncate">
                        {[watch('name'), selectedCustomer?.company_name].filter(Boolean).join(' · ') || 'Nessun dato inserito'}
                      </span>
                    )}
                    {showOfferInfo
                      ? <ChevronUp className="w-4 h-4 text-red-700 flex-shrink-0 ml-auto" />
                      : <ChevronDown className="w-4 h-4 text-red-700 flex-shrink-0 ml-auto" />}
                  </button>
                </div>

                <form
                  onSubmit={handleSubmit(handleMainFormSubmit)}
                  className={`mt-1 ${showOfferInfo ? '' : 'hidden'}`}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
                    <div>
                      <Label htmlFor="name" className="text-xs font-medium text-gray-700">
                        Nome Offerta *
                      </Label>
                      <Input
                        id="name"
                        {...register('name')}
                        className={`h-6 text-xs ${errors.name ? 'border-red-500' : ''}`}
                        placeholder="es. Offerta 2024"
                      />
                      {errors.name && (
                        <p className="text-xs text-red-600 mt-1">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs font-medium text-gray-700">Cliente *</Label>
                      <div className="flex items-center space-x-1">
                        {selectedCustomer ? (
                          <div className="flex items-center space-x-1 bg-white border rounded px-1 py-0.5 h-6 flex-1">
                            <Building className="w-3 h-3 text-gray-500" />
                            <span className="text-xs text-gray-700 truncate">
                              {selectedCustomer.company_name}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedCustomer(null);
                                setSelectedCustomerId('');
                                setValue('customer_id', '');
                              }}
                              className="h-3 w-3 p-0 hover:bg-gray-100"
                            >
                              <X className="h-2 w-2" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleOpenCustomerModal}
                            className={`h-6 text-xs flex-1 justify-start px-1 ${errors.customer_id ? 'border-red-500' : ''}`}
                          >
                            <Building className="w-3 h-3 mr-1" />
                            Cliente
                          </Button>
                        )}
                      </div>
                      {errors.customer_id && (
                        <p className="text-xs text-red-600 mt-1">{errors.customer_id.message}</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs font-medium text-gray-700">
                        <UserCheck className="w-3 h-3 inline mr-1" />
                        Commerciale
                      </Label>
                      <Select value={selectedCommercialeId} onValueChange={setSelectedCommercialeId}>
                        <SelectTrigger className="h-6 text-xs">
                          <SelectValue placeholder="Assegna a..." />
                        </SelectTrigger>
                        <SelectContent>
                          {commerciali.map(c => {
                            const displayName = c.full_name
                              ? c.full_name
                              : c.email.split('@')[0].split(/[._-]/).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
                            return (
                              <SelectItem key={c.id} value={c.id}>
                                {displayName} ({c.email.split('@')[0]})
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-end sm:col-span-2 md:col-span-1">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="h-9 w-full sm:h-6 sm:w-auto text-xs px-3 bg-green-600 hover:bg-green-700"
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          currentPriceList ? 'Aggiorna' : 'Salva'
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              </div>

              {/* Prodotti + Pannello Calcolo - Layout a 2 colonne */}
              <div className="flex-1 flex flex-col lg:flex-row gap-2 mt-0 mb-0 min-h-0">
                {/* Colonna Sinistra: Prodotti nel Listino */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-1 flex-1 flex flex-col min-h-0 min-w-0">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1 flex-shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <Package className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <h3 className="text-base sm:text-lg font-semibold text-green-800">
                        Prodotti nell'Offerta ({currentPriceList?.price_list_items?.length || 0})
                      </h3>
                    </div>
                    {currentPriceList && (
                      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleOpenManualLineModal()}
                          className="h-9 flex-1 sm:h-7 sm:flex-none text-xs px-3 border-blue-300 text-blue-700 hover:bg-blue-50"
                        >
                          <PencilLine className="w-3 h-3 mr-1" />
                          Riga manuale
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleOpenNewProductModal}
                          disabled={!user?.id}
                          className="h-9 flex-1 sm:h-7 sm:flex-none text-xs px-3 border-blue-300 text-blue-700 hover:bg-blue-50"
                        >
                          <PackagePlus className="w-3 h-3 mr-1" />
                          Nuovo prodotto
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setShowProductModal(true)}
                          className="h-9 flex-1 sm:h-7 sm:flex-none text-xs px-3 bg-blue-600 hover:bg-blue-700"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Aggiungi Prodotti
                        </Button>
                      </div>
                    )}
                  </div>

                  {!currentPriceList ? (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 text-green-600 mx-auto mb-4" />
                      <p className="text-sm text-green-700">
                        Salva prima le informazioni dell'offerta per gestire i prodotti
                      </p>
                    </div>
                  ) : currentPriceList.price_list_items && currentPriceList.price_list_items.length > 0 ? (
                    <>
                      {/* Controlli di ordinamento e filtro categoria */}
                      <div className="flex items-center gap-2 mb-2 flex-shrink-0 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0 flex-1 sm:flex-none">
                          <Label className="text-xs text-gray-600 flex-shrink-0">Ordina per:</Label>
                          <Select value={sortField} onValueChange={(value: 'code' | 'name') => setSortField(value)}>
                            <SelectTrigger className="h-6 text-xs w-full sm:w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="name">Nome</SelectItem>
                              <SelectItem value="code">Codice</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                            className="h-6 text-xs px-2"
                            title={sortDirection === 'asc' ? 'Crescente' : 'Decrescente'}
                          >
                            {sortDirection === 'asc' ? (
                              <ArrowUp className="w-3 h-3" />
                            ) : (
                              <ArrowDown className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                        <div className="flex items-center gap-2 min-w-0 flex-1 sm:flex-none">
                          <Label className="text-xs text-gray-600 flex-shrink-0">Categoria:</Label>
                          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                            <SelectTrigger className="h-6 text-xs w-full sm:w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Tutte le categorie</SelectItem>
                              {Array.from(new Set(currentPriceList.price_list_items.map(item => item.products.category).filter(Boolean))).map(category => (
                                <SelectItem key={category} value={category || ''}>
                                  {category}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="printByCategory"
                            checked={printByCategory}
                            onChange={(e) => setPrintByCategory(e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <Label htmlFor="printByCategory" className="text-xs text-gray-600 cursor-pointer">
                            Stampa per categorie
                          </Label>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto space-y-1 pr-1 min-h-0">
                        {(() => {
                          const filteredItems = currentPriceList.price_list_items
                            ? [...currentPriceList.price_list_items].filter(item => {
                              if (selectedCategory === 'all') return true;
                              return item.products.category === selectedCategory;
                            })
                            : [];

                          const sortItems = (items: PriceListItemWithProduct[]) => {
                            return [...items].sort((a, b) => {
                              let comparison = 0;
                              if (sortField === 'code') {
                                comparison = (a.products.code || '').localeCompare(b.products.code || '');
                              } else {
                                comparison = (a.products.name || '').localeCompare(b.products.name || '');
                              }
                              return sortDirection === 'asc' ? comparison : -comparison;
                            });
                          };

                          if (printByCategory) {
                            const groupedByCategory = filteredItems.reduce<Record<string, PriceListItemWithProduct[]>>((acc, item) => {
                              const category = item.products.category || 'Senza categoria';
                              if (!acc[category]) {
                                acc[category] = [];
                              }
                              acc[category].push(item);
                              return acc;
                            }, {});

                            const sortedCategories = Object.keys(groupedByCategory).sort();

                            return sortedCategories.map(category => (
                              <div key={`category-${category}`} className="space-y-1">
                                <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs font-semibold px-3 py-1 rounded">
                                  {category}
                                </div>
                                {sortItems(groupedByCategory[category]).map(item => renderProductCard(item))}
                              </div>
                            ));
                          }

                          return sortItems(filteredItems).map(item => renderProductCard(item));
                        })()}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 text-green-600 mx-auto mb-4" />
                      <p className="text-sm text-green-700 mb-4">
                        Nessun prodotto in questa offerta
                      </p>
                      <p className="text-xs text-green-600">
                        Clicca "Aggiungi Prodotti", oppure "Riga manuale" per una voce libera
                      </p>
                    </div>
                  )}
                </div>

                {/* Colonna Destra: Pannello Calcolo sempre visibile */}
                {currentPriceList && currentPriceList.price_list_items && currentPriceList.price_list_items.length > 0 && (
                  <div className="w-full lg:w-56 flex-shrink-0 flex flex-col gap-2 order-first lg:order-none">
                    <div className="bg-slate-800 text-white rounded-lg p-3 lg:sticky lg:top-0">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-300 mb-3">Riepilogo Offerta</h4>

                      <div className="space-y-2 text-xs">
                        {(() => {
                          const items = currentPriceList.price_list_items;
                          const canoneItems = items.filter(i => (i.products.category || '').toLowerCase() === 'canone');
                          const prodottiItems = items.filter(i => (i.products.category || '').toLowerCase() !== 'canone');
                          const hasCanone = canoneItems.length > 0;

                          const calcTotal = (list: typeof items) =>
                            list.reduce((acc, item) => acc + priceListLineTotalAmount(item), 0);

                          const formatEur = (n: number) =>
                            new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(n);

                          const hasConsumptionLines = items.some(i => isConsumptionCategoryItem(i));

                          if (!hasCanone) {
                            // Nessun canone: totale unico come prima
                            const totalAmount = calcTotal(items);
                            const noVat = noVatForfettarioWatched;
                            const vatAmount = noVat ? 0 : totalAmount * 0.22;
                            const totalWithVat = totalAmount + vatAmount;
                            return (
                              <>
                                <div className="flex justify-between items-center text-slate-300">
                                  <span>Prodotti</span>
                                  <span className="font-mono font-semibold text-white">{items.length}</span>
                                </div>
                                <div className="border-t border-slate-600 my-2" />
                                {noVat ? (
                                  <div className="flex justify-between items-center">
                                    <span className="font-bold text-sm text-white">TOTALE <span className="font-normal text-[10px] text-slate-400">(senza IVA)</span></span>
                                    <span className="font-mono font-bold text-sm text-green-400">{formatEur(totalAmount)}</span>
                                  </div>
                                ) : (
                                  <>
                                    <div className="flex justify-between items-center text-slate-300">
                                      <span>Imponibile</span>
                                      <span className="font-mono text-white">{formatEur(totalAmount)}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-slate-300">
                                      <span>IVA (22%)</span>
                                      <span className="font-mono text-white">{formatEur(vatAmount)}</span>
                                    </div>
                                    <div className="border-t border-slate-500 my-2" />
                                    <div className="flex justify-between items-center">
                                      <span className="font-bold text-sm text-white">TOTALE</span>
                                      <span className="font-mono font-bold text-sm text-green-400">{formatEur(totalWithVat)}</span>
                                    </div>
                                  </>
                                )}
                                {hasConsumptionLines && (
                                  <p className="text-[10px] text-slate-400 leading-snug pt-2 border-t border-slate-600 mt-2">
                                    Il totale non include i prodotti con categoria «{PRICE_LIST_CONSUMPTION_LABEL}» (prezzo variabile).
                                  </p>
                                )}
                              </>
                            );
                          }

                          // Con canone: due totali separati
                          const noVat = noVatForfettarioWatched;
                          const vatRate = noVat ? 0 : 0.22;
                          const prodottiTotal = calcTotal(prodottiItems);
                          const prodottiVat = prodottiTotal * vatRate;
                          const prodottiWithVat = prodottiTotal + prodottiVat;

                          const canoneMonthlyTotal = calcTotal(canoneItems);
                          const canonePeriodTotal = canoneMonthlyTotal * canoneMonths;
                          const canoneVat = canonePeriodTotal * vatRate;
                          const canoneWithVat = canonePeriodTotal + canoneVat;

                          const grandTotal = prodottiWithVat + canoneWithVat;

                          const handleCanoneMonthsChange = async (months: number) => {
                            setCanoneMonths(months);
                            if (currentPriceList?.id) {
                              await (supabase.from('price_lists') as any)
                                .update({ canone_months: months })
                                .eq('id', currentPriceList.id);
                            }
                          };

                          return (
                            <>
                              {/* Prodotti */}
                              <div className="flex justify-between items-center text-slate-300">
                                <span>Prodotti</span>
                                <span className="font-mono font-semibold text-white">{prodottiItems.length}</span>
                              </div>
                              <div className="border-t border-slate-600 my-2" />
                              <div className="flex justify-between items-center text-slate-300">
                                <span>Imponibile</span>
                                <span className="font-mono text-white">{formatEur(prodottiTotal)}</span>
                              </div>
                              {!noVat && (
                                <div className="flex justify-between items-center text-slate-300">
                                  <span>IVA (22%)</span>
                                  <span className="font-mono text-white">{formatEur(prodottiVat)}</span>
                                </div>
                              )}
                              <div className="border-t border-slate-500 my-2" />
                              <div className="flex justify-between items-center">
                                <span className="font-bold text-xs text-white">TOT. PRODOTTI</span>
                                <span className="font-mono font-bold text-xs text-green-400">{formatEur(prodottiWithVat)}</span>
                              </div>

                              {/* Canone */}
                              <div className="border-t border-slate-500 my-3" />
                              <div className="flex justify-between items-center text-slate-300">
                                <span>Canone</span>
                                <span className="font-mono font-semibold text-white">{canoneItems.length}</span>
                              </div>
                              <div className="flex justify-between items-center text-slate-300 mt-1">
                                <span>Mensile</span>
                                <span className="font-mono text-white">{formatEur(canoneMonthlyTotal)}</span>
                              </div>
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-slate-300 text-xs">Mensilità</span>
                                <select
                                  value={canoneMonths}
                                  onChange={(e) => handleCanoneMonthsChange(Number(e.target.value))}
                                  className="ml-auto bg-slate-700 text-white text-xs rounded px-1.5 py-0.5 border border-slate-500 font-mono"
                                >
                                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="border-t border-slate-600 my-2" />
                              <div className="flex justify-between items-center text-slate-300">
                                <span>Imponibile ({canoneMonths} mesi)</span>
                                <span className="font-mono text-white">{formatEur(canonePeriodTotal)}</span>
                              </div>
                              {!noVat && (
                                <div className="flex justify-between items-center text-slate-300">
                                  <span>IVA (22%)</span>
                                  <span className="font-mono text-white">{formatEur(canoneVat)}</span>
                                </div>
                              )}
                              <div className="border-t border-slate-500 my-2" />
                              <div className="flex justify-between items-center">
                                <span className="font-bold text-xs text-white">TOT. CANONE</span>
                                <span className="font-mono font-bold text-xs text-amber-400">{formatEur(canoneWithVat)}</span>
                              </div>

                              {/* Grand Total */}
                              <div className="border-t-2 border-slate-400 my-3" />
                              <div className="flex justify-between items-center">
                                <span className="font-bold text-sm text-white">TOTALE</span>
                                <span className="font-mono font-bold text-sm text-green-400">{formatEur(grandTotal)}</span>
                              </div>
                              {hasConsumptionLines && (
                                <p className="text-[10px] text-slate-400 leading-snug pt-2 border-t border-slate-600 mt-2">
                                  Il totale non include i prodotti con categoria «{PRICE_LIST_CONSUMPTION_LABEL}» (prezzo variabile).
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Dettaglio per categoria */}
                    {(() => {
                      const items = currentPriceList.price_list_items;
                      const categories = Array.from(new Set(items.map(i => i.products.category || 'Senza categoria'))).sort();
                      if (categories.length <= 1) return null;
                      return (
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Per Categoria</h4>
                          <div className="space-y-1.5">
                            {categories.map(cat => {
                              const catItems = items.filter(i => (i.products.category || 'Senza categoria') === cat);
                              const catTotal = catItems.reduce(
                                (acc, item) => acc + priceListLineTotalAmount(item),
                                0
                              );
                              return (
                                <div key={cat} className="flex justify-between items-center text-xs">
                                  <span className="text-gray-600 truncate mr-2">{cat}</span>
                                  <span className="font-mono text-gray-900 flex-shrink-0">
                                    {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(catTotal)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Condizioni contrattuali - compatta e richiudibile */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg px-2 py-1 mt-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowConditions((open) => !open)}
                  aria-expanded={showConditions}
                  className="flex w-full items-center gap-2 min-w-0 text-left py-0.5"
                >
                  <FileText className="w-4 h-4 text-orange-600 flex-shrink-0" />
                  <h3 className="text-sm font-semibold text-orange-800 flex-shrink-0">Condizioni Contrattuali</h3>
                  {!showConditions && (
                    <span className="text-xs text-orange-700/80 truncate">{conditionsSummary}</span>
                  )}
                  {showConditions
                    ? <ChevronUp className="w-4 h-4 text-orange-700 flex-shrink-0 ml-auto" />
                    : <ChevronDown className="w-4 h-4 text-orange-700 flex-shrink-0 ml-auto" />}
                </button>

                {showConditions && (
                  <div className="mt-2 space-y-2 pb-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      <div>
                        <Label htmlFor="payment_conditions" className="text-[11px] font-medium text-gray-600">
                          Pagamento
                        </Label>
                        <Input
                          id="payment_conditions"
                          {...register('payment_conditions', { onChange: () => scheduleSaveConditions(false) })}
                          className="h-7 text-xs"
                          placeholder="es. 30 giorni, Bonifico anticipato"
                          onBlur={() => scheduleSaveConditions(true)}
                        />
                      </div>

                      <div>
                        <Label htmlFor="delivery_conditions" className="text-[11px] font-medium text-gray-600">
                          Durata del contratto
                        </Label>
                        <Input
                          id="delivery_conditions"
                          {...register('delivery_conditions', { onChange: () => scheduleSaveConditions(false) })}
                          className="h-7 text-xs"
                          placeholder="es. 1 anno, 6 mesi"
                          onBlur={() => scheduleSaveConditions(true)}
                        />
                      </div>

                      <div>
                        <Label htmlFor="valid_from" className="text-[11px] font-medium text-gray-600">
                          Data inizio licenza
                        </Label>
                        <Input
                          id="valid_from"
                          type="text"
                          placeholder="dd/mm/yyyy"
                          {...register('valid_from', { onChange: () => scheduleSaveConditions(false) })}
                          className={`h-7 text-xs ${errors.valid_from ? 'border-red-500' : ''}`}
                          onBlur={() => scheduleSaveConditions(true)}
                        />
                        {errors.valid_from && (
                          <p className="text-[10px] text-red-600 mt-0.5">{errors.valid_from.message}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="valid_until" className="text-[11px] font-medium text-gray-600">
                          Data fine licenza
                        </Label>
                        <Input
                          id="valid_until"
                          type="text"
                          placeholder="dd/mm/yyyy"
                          {...register('valid_until', { onChange: () => scheduleSaveConditions(false) })}
                          className="h-7 text-xs"
                          onBlur={() => scheduleSaveConditions(true)}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="notes" className="text-[11px] font-medium text-gray-600">
                        Note
                      </Label>
                      <Textarea
                        id="notes"
                        {...register('notes', { onChange: () => scheduleSaveConditions(false) })}
                        className="min-h-[44px] text-xs resize-y"
                        rows={2}
                        placeholder="Note aggiuntive per il cliente..."
                        onBlur={() => scheduleSaveConditions(true)}
                      />
                    </div>

                    <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-5 sm:gap-y-1.5">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="print_conditions"
                          {...register('print_conditions', { onChange: () => scheduleSaveConditions(true) })}
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <Label htmlFor="print_conditions" className="text-xs text-gray-700 cursor-pointer">
                          Stampa condizioni
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="print_discount"
                          {...register('print_discount', { onChange: () => scheduleSaveConditions(true) })}
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <Label htmlFor="print_discount" className="text-xs text-gray-700 cursor-pointer">
                          Stampa sconti
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="print_attachments"
                          {...register('print_attachments', { onChange: () => scheduleSaveConditions(true) })}
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <Label htmlFor="print_attachments" className="text-xs text-gray-700 cursor-pointer">
                          Allega contratto + privacy
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="no_vat_forfettario"
                          {...register('no_vat_forfettario', { onChange: () => scheduleSaveConditions(true) })}
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <Label htmlFor="no_vat_forfettario" className="text-xs text-gray-700 cursor-pointer" title="Totali senza scorporo IVA e clausola di assenza IVA su PDF, anteprima ed Excel">
                          Offerta senza IVA (regime forfettario)
                        </Label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap justify-end items-center gap-2 border-t bg-gray-50 px-2 py-2 flex-shrink-0">
          <div className="w-full sm:w-auto sm:mr-auto flex items-center gap-2">
            <Label className="text-xs">Stato</Label>
            <Select
              value={offerStatus}
              onValueChange={async (v) => {
                const next = v as typeof offerStatus;
                setOfferStatus(next);
                if (priceListId) {
                  await (supabase.from('price_lists') as any)
                    .update({ status: next })
                    .eq('id', priceListId);
                }
              }}
              disabled={!priceListId}
            >
              <SelectTrigger className="h-8 sm:h-7 text-xs flex-1 sm:flex-none sm:w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Bozza</SelectItem>
                <SelectItem value="sent">Inviata</SelectItem>
                <SelectItem value="accepted">Accettata</SelectItem>
                <SelectItem value="partially_invoiced">Parz. fatturata</SelectItem>
                <SelectItem value="invoiced">Fatturata</SelectItem>
                <SelectItem value="expired">Scaduta</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            onClick={() => setShowInvoiceModal(true)}
            className="h-9 sm:h-7 text-xs px-3 sm:px-4 flex-1 sm:flex-none bg-amber-600 hover:bg-amber-700 text-white"
            disabled={!priceListId || (offerStatus !== 'accepted' && offerStatus !== 'partially_invoiced' && offerStatus !== 'invoiced')}
            title={offerStatus !== 'accepted' && offerStatus !== 'partially_invoiced' && offerStatus !== 'invoiced' ? 'Disponibile solo per offerte accettate' : ''}
          >
            <FileText className="w-3 h-3 mr-1" />
            Crea Fattura
          </Button>
          <Button
            type="button"
            onClick={() => setShowPreviewModal(true)}
            className="h-9 sm:h-7 text-xs px-3 sm:px-4 flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white"
            disabled={!priceListId}
          >
            <Eye className="w-3 h-3 mr-1" />
            Anteprima
          </Button>
          <Button
            type="button"
            onClick={handleSubmit(handleMainFormSubmit)}
            className="h-9 sm:h-7 text-xs px-3 sm:px-4 flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white"
          >
            {priceListId ? 'Salva' : 'Crea Offerta'}
          </Button>
          <Button
            type="button"
            onClick={handleSaveAndClose}
            className="h-9 sm:h-7 text-xs px-3 sm:px-4 flex-1 sm:flex-none bg-gray-600 hover:bg-gray-700 text-white"
          >
            Salva e Chiudi
          </Button>
        </div>
      </FloatingWindow>

      {/* Customer Selection Modal */}
      <CustomerSelectionModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onCustomerSelect={handleCustomerSelect}
        selectedCustomerId={selectedCustomerId}
        title="Seleziona Cliente per Offerta"
        description="Scegli il cliente a cui assegnare questa offerta"
      />

      {/* Product Selection Modal */}
      {currentPriceList && (
        <ProductSelectionModal
          isOpen={showProductModal}
          onClose={() => setShowProductModal(false)}
          onProductSelect={() => { }} // Non usato in questa implementazione
          priceListId={currentPriceList.id}
          currentCustomer={currentPriceList.customer as any}
          onProductsAdded={() => fetchPriceListDetails(currentPriceList.id)}
        />
      )}

      {/* Nuovo prodotto: lo crea nel catalogo e lo aggiunge all'offerta */}
      {currentPriceList && (
        <ProductFormModal
          isOpen={showNewProductModal}
          onClose={() => setShowNewProductModal(false)}
          onSaveSuccess={handleNewProductCreated}
          categories={productCategories}
        />
      )}

      {/* Manual Line Modal */}
      {currentPriceList && (
        <ManualLineModal
          isOpen={showManualLineModal}
          onClose={handleCloseManualLineModal}
          onSubmit={handleSaveManualLine}
          isSaving={isSavingManualLine}
          initialValues={
            editingManualItem
              ? {
                  code: editingManualItem.custom_code ?? '',
                  name: editingManualItem.custom_name ?? '',
                  description: editingManualItem.custom_description ?? '',
                  unit: editingManualItem.custom_unit ?? '',
                  category: editingManualItem.custom_category ?? '',
                  price: Number(editingManualItem.price ?? 0),
                  quantity: Number(editingManualItem.min_quantity ?? 1),
                  discountPercentage: Number(editingManualItem.discount_percentage ?? 0),
                }
              : null
          }
        />
      )}

      {/* Create Invoice From Offer Modal */}
      {priceListId && (
        <CreateInvoiceFromOfferModal
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          priceListId={priceListId}
          onCreated={(invoice) => {
            // Lo stato reale (partially_invoiced per acconto, invoiced per saldo/completa)
            // viene riletto dal DB da fetchPriceListDetails.
            if (priceListId) fetchPriceListDetails(priceListId);
            setReviewInvoiceId(invoice.id);
          }}
        />
      )}

      {/* Review/edit the just-created invoice before SDI send */}
      <InvoiceDetailPage
        isOpen={!!reviewInvoiceId}
        onClose={() => setReviewInvoiceId(null)}
        invoiceId={reviewInvoiceId ?? undefined}
        mode="edit"
        onSaveSuccess={() => {
          if (priceListId) fetchPriceListDetails(priceListId);
        }}
      />

      {/* Price List Preview Modal */}
      <PriceListPrintView
        isOpen={showPreviewModal && !!priceListId}
        onClose={() => setShowPreviewModal(false)}
        priceListId={priceListId || ''}
        sortField={sortField}
        sortDirection={sortDirection}
        selectedCategory={selectedCategory}
        printByCategory={printByCategory}
        canoneMonths={canoneMonths}
      />
    </>
  );
}
export default PriceListDetailPage;
